import './CategoryEditor.scss';
import {useEffect, useState} from 'react';
import classnames from 'classnames';
import {ByzzerButton} from '@byzzer/ui-components';
import {useTenantApi} from '@/hooks/useTenantApi';
import {useLocation, useNavigate} from 'react-router-dom';
import Close from '@/images/icons/close-circle-icon.svg';
import {useUser} from '@/contexts/UserContext';
import {openErrorModal} from '@/components/form/ByzzerModal';
import DashboardContent from "@/components/dashboard/DashboardContent";
import CategorySelector from "@/views/onboarding/CategorySelector";
import {ByzzerMask} from "@/components/ByzzerMask/ByzzerMask";

const baseClassName = 'category-editor';
const CategoryEditor = () => {
    const {getMySubscriptionUsage, updateCategories} = useTenantApi();
    const location = useLocation();
    const canEditCategories = location.state?.canEditCategories;
    const uneditableCategories = location.state?.uneditableCategories ?? [];
    const {categories: currentCategories } = useUser();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [maxCategories, setMaxCategories] = useState(0);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        setCategories(currentCategories);
        fetchCategories();
    }, []);

    async function fetchCategories() {
        setBusy(true);
        try {
            const {categories: categoryUsage = {}} = await getMySubscriptionUsage();
            setMaxCategories(categoryUsage.limit ?? 0);
        } catch (err) {
            console.error(err);
        } finally {
            setBusy(false);
        }
    }

    function cancel() {
        navigate(-1);
    }

    async function save() {
        try {
            setBusy(true);
            await updateCategories(categories);
            navigate(-1);
        } catch (err) {
            // todo: add handling for the various error cases
            switch (err.code) {
                case 'company_not_found':
                    // this should never happen except maybe in the lower environments
                    await openErrorModal({
                        title: `Company not found`,
                        content: (
                            <>
                                <p>
                                    We couldn't find your company, so we were unable to save you choices at this time.
                                </p>
                                <p>Please try again later or contact customer support.</p>
                            </>
                        ),
                        errorId: err.id
                    });
                    break;
                default:
                    openErrorModal({
                        title: 'Failed To Save Your Choices',
                        content: (
                            <>
                                <p>Some unexpected happened while saving your choices.</p>
                                <p>Our support time is looking into the issue.</p>
                                <p>Please contact customer support if you have any questions.</p>
                            </>
                        ),
                        errorId: err.errorId
                    });
            }
        } finally {
            setBusy(false);
        }
    }

    function handleOptionClick(value, categories) {
        let uniqueCategories = [...new Set(categories)];
        uniqueCategories.forEach((item, index) => {
            if (item === value) {
                uniqueCategories.splice(index, 1);
            }
        });
        let editableCategories = uniqueCategories.filter((item) => !uneditableCategories.includes(item));
        let tempCategoryOption = uniqueCategories.map((option) => {
            const {value = option, display = option} = option;
            if (canEditCategories) {
                return (
                    <div className={'configure-scope-wrapper__categories-option'}>
                        <div key={value}>{display}</div>
                        <img
                            className={'configure-scope-wrapper__categories-option-close'}
                            src={Close}
                            onClick={() => handleOptionClick(option, uniqueCategories)}
                            alt="close"
                        />
                    </div>
                );
            } else {
                return (
                    <>
                        {!uneditableCategories.includes(option) && (
                            <div className={'configure-scope-wrapper__categories-option'}>
                                <div key={value}>{display}</div>
                                <img
                                    className={'configure-scope-wrapper__categories-option-close'}
                                    src={Close}
                                    onClick={() => handleOptionClick(option, uniqueCategories)}
                                    alt="close"
                                />
                            </div>
                        )}
                    </>
                );
            }
        });
        if (editableCategories.length === 0 && !canEditCategories) {
            tempCategoryOption = [];
        }
        setCategories(uniqueCategories);
    }

    function updateProductScope(data) {
        data.forEach((item) => {
            categories.push(item);
        });
        let uniqueCategories = [...new Set(categories)];
        let tempCategoryOption =
            uniqueCategories.length === 0
                ? []
                : uniqueCategories.map((option) => {
                    const {value = option, display = option} = option;
                    if (canEditCategories) {
                        return (
                            <div className={'configure-scope-wrapper__categories-option'}>
                                <div key={value}>{display}</div>
                                <img
                                    className={'configure-scope-wrapper__categories-option-close'}
                                    src={Close}
                                    onClick={() => handleOptionClick(option, uniqueCategories)}
                                    alt="close"
                                />
                            </div>
                        );
                    } else {
                        return (
                            <>
                                {!uneditableCategories.includes(option) && (
                                    <div className={'configure-scope-wrapper__categories-option'}>
                                        <div key={value}>{display}</div>
                                        <img
                                            className={'configure-scope-wrapper__categories-option-close'}
                                            src={Close}
                                            onClick={() => handleOptionClick(option, uniqueCategories)}
                                            alt="close"
                                        />
                                    </div>
                                )}
                            </>
                        );
                    }
                });
        setCategories(uniqueCategories);
    }

    function onSelectionChange(categories) {
        setCategories(categories);
    }

    return (
        <DashboardContent title={'Edit Your Categories'} className={classnames(`${baseClassName}`, {
            [`${baseClassName}--loading`]: busy
        })}>
            <ByzzerMask loading={busy}/>
            <CategorySelector
                maxCategoryCount={maxCategories}
                categories={categories}
                onSelectionChange={onSelectionChange}
                uneditableCategories={uneditableCategories}
                productScopeSubtitle={`Your subscription includes ${maxCategories} categories. You can add-on more in the shop.`}
                submitButtonLabel={'Add'}
                displayTreeButtonLabel={'Confirm categories'}
            />
            <div className={`${baseClassName}__actions`}>
                <ByzzerButton onClick={cancel} type={'negative'}>Cancel</ByzzerButton>
                <ByzzerButton onClick={save}>Save Changes</ByzzerButton>
            </div>
        </DashboardContent>
    );
};
export default CategoryEditor;
